import useMutation from 'hooks/use-mutation';
import useSaveNodeInCache from 'components/canvas/hooks/use-save-node-in-cache';
import useFetchSurfaceNodesFromCache from 'components/canvas/hooks/use-fetch-surface-nodes-from-cache';
import { ejectSurfaceNodeFromSurfaceProtocolNodeMutation } from 'graphql/surface-node/mutations/surface-protocol-node/eject-surface-node-from-surface-protocol-node';
import { compose, set } from 'lodash/fp';
import { omit } from 'lodash';
import {
  SurfaceNodeType,
  SurfaceNodeUnion,
  SurfaceProtocolNode,
} from 'graphql/types';

export default function useEjectSurfaceNodeFromSurfaceProtocolNode() {
  const saveNodeInCache = useSaveNodeInCache();

  const fetchSurfaceNodesFromCache = useFetchSurfaceNodesFromCache();

  const [ejectSurfaceNodeFromSurfaceProtocolNode] = useMutation(
    ejectSurfaceNodeFromSurfaceProtocolNodeMutation,
  );

  function fetchProtocolsFromCache(): SurfaceProtocolNode[] {
    return fetchSurfaceNodesFromCache().filter(
      (node) => node.type === SurfaceNodeType.Protocol,
    );
  }

  function removeNodeFromProtocol(protocol: SurfaceNodeUnion) {
    const newProtocolNode = compose(
      set('contains', null),
      set('updatedAt', new Date().toISOString()),
    )(protocol);

    saveNodeInCache(newProtocolNode);
  }

  function isReferenceOnSameSurfaceAsOriginal(
    protocolNode: SurfaceProtocolNode,
  ): boolean {
    const isOriginalOnSameSurface =
      protocolNode?.surface?.id === protocolNode?.originalNode?.surface?.id;

    return Boolean(protocolNode?.isReference && isOriginalOnSameSurface);
  }

  function removeNodeFromProtocolReferencesOnSameSurface(
    originalProtocol: SurfaceProtocolNode,
  ) {
    const protocols = fetchProtocolsFromCache();

    const protocolReferences = protocols.filter(
      ({ originalNode }) => originalNode?.id === originalProtocol.id,
    );

    protocolReferences.forEach((protocol) => removeNodeFromProtocol(protocol));
  }

  return async (params) => {
    const input = omit(params?.variables?.input, ['position']);

    let { protocolNode } = input;

    if (isReferenceOnSameSurfaceAsOriginal(protocolNode)) {
      protocolNode = protocolNode?.originalNode;
    }

    input.protocolNode = protocolNode;

    removeNodeFromProtocol(input.protocolNode);

    removeNodeFromProtocolReferencesOnSameSurface(protocolNode);

    params.variables.ejectInput = {
      node: params.variables.input.node.id,
      protocolNode: params.variables.input.protocolNode.id,
      position: params.variables.input.position,
    };

    params.variables.positionInput = {
      nodeId: params.variables.input.node.id,
      position: params.variables.input.position,
    };

    delete params.variables.input;

    return ejectSurfaceNodeFromSurfaceProtocolNode(params);
  };
}
